import { useState } from "react";
import { type CSSObject } from "@emotion/react";
import {
  Breakpoints,
  Button,
  Colors,
  fontLarge,
  fontMedium,
  Icon,
  RadioList,
} from "swing-components";

import { translate } from "~utils";

/********* Types ********/
export type MealBreakOption = (typeof MealBreakOptions)[keyof typeof MealBreakOptions];

type NonCompliantMealBreakActionSheetProps = {
  onDismiss: () => void;
  onSubmit: () => void;
  /**
   * Indicates whether the submission action is currently pending.
   * Used to show loading states and disable interaction.
   */
  isSubmissionPending?: boolean;
  /**
   * Callback fired when the user selects a meal break option.
   * @param option - The selected meal break option value
   */
  onOptionChange: (option: MealBreakOption) => void;
};

export function NonCompliantMealBreakActionSheet(props: NonCompliantMealBreakActionSheetProps) {
  const { onDismiss, onSubmit, isSubmissionPending = false, onOptionChange } = props;

  const [mealBreakOption, setMealBreakOption] = useState<MealBreakOption | undefined>();

  // Handle option change with callback to parent
  function handleOptionChange(value: MealBreakOption) {
    setMealBreakOption(value);
    onOptionChange(value);
  }

  /********* Render ********/
  return (
    <>
      {/* Header and content */}
      <div css={{ display: "flex", flexDirection: "column", position: "relative" }}>
        {/* Close button */}
        <div css={{ position: "absolute", top: 16, right: 16, zIndex: 3 }}>
          <button onClick={onDismiss} css={{ background: "none" }}>
            <Icon name="Close" color={Colors.blue500} />
          </button>
        </div>

        {/* Header */}
        <div css={styles.header}>
          <h2
            css={{
              ...fontLarge({ fontWeight: 500 }),
              // Override global h2 font-size (24px !important) with custom size
              fontSize: "20px !important",
              margin: 0,
              [Breakpoints.mediumDeviceAndUp]: { maxWidth: "100%", paddingRight: 0 },
            }}
          >
            {translate("TIMESHEET_NON_COMPLIANT_MEAL_BREAK_ACTION_SHEET_TITLE")}
          </h2>
        </div>

        {/* Content */}
        <div css={styles.body}>
          <RadioList
            groupName="meal-break-radio-input"
            value={mealBreakOption}
            options={[
              {
                value: MealBreakOptions.CONFIRMED,
                label: translate("TIMESHEET_NON_COMPLIANT_MEAL_BREAK_ACTION_SHEET_OPTION_1"),
                description:
                  mealBreakOption === MealBreakOptions.CONFIRMED
                    ? translate("TIMESHEET_NON_COMPLIANT_MEAL_BREAK_ACTION_SHEET_OPTION_1_NOTE")
                    : undefined,
              },
              {
                value: MealBreakOptions.DIRECTED_TO_WORK_THROUGH,
                label: translate("TIMESHEET_NON_COMPLIANT_MEAL_BREAK_ACTION_SHEET_OPTION_2"),
              },
              {
                value: MealBreakOptions.SKIPPED,
                label: translate("TIMESHEET_NON_COMPLIANT_MEAL_BREAK_ACTION_SHEET_OPTION_3"),
                description: translate(
                  "TIMESHEET_NON_COMPLIANT_MEAL_BREAK_ACTION_SHEET_OPTION_3_NOTE",
                ),
              },
            ]}
            onChange={(value) => handleOptionChange(value as MealBreakOption)}
          />
        </div>
      </div>

      {/* Footer */}
      <div css={styles.footer}>
        <div css={styles.buttonGroup}>
          <div css={styles.actionButton}>
            <Button
              type="primary"
              isFluid
              onClick={onSubmit}
              status={!mealBreakOption ? "disabled" : isSubmissionPending ? "loading" : "enabled"}
              icon={
                mealBreakOption === MealBreakOptions.CONFIRMED ? { name: "Arrow Left" } : undefined
              }
            >
              {mealBreakOption === MealBreakOptions.CONFIRMED
                ? translate("TIMESHEET_RETURN_TO_ENTRY_LABEL")
                : translate("TIMESHEET_SUBMIT_ENTRY_LABEL")}
            </Button>
          </div>

          {/* Secondary Button only displays on desktop */}
          <div css={[styles.actionButton, styles.secondaryButton]}>
            <Button
              type="secondary"
              isFluid
              onClick={onDismiss}
              status={isSubmissionPending ? "loading" : "enabled"}
            >
              {translate("LABEL_CLOSE")}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

/********* Constants ********/
const MealBreakOptions = {
  CONFIRMED: "confirmed-break",
  DIRECTED_TO_WORK_THROUGH: "SUB_WORKED_MEAL_BREAK",
  SKIPPED: "SUB_WAIVED_MEAL_BREAK",
} as const;

/********* Styles ********/
const styles = {
  header: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    backgroundColor: Colors.white200,
    padding: "calc(32px + var(--ion-safe-area-top, 0)) 16px 16px",
    [Breakpoints.mediumDeviceAndUp]: {
      padding: "16px 16px 8px",
    },
  },
  body: {
    ...fontMedium(),
    lineHeight: "24px",
    padding: "0 16px 0",
    zIndex: 1,
    overflowY: "auto",
    maxHeight: "calc(90dvh - 164px - var(--ion-safe-area-bottom, 0))", // header height + footer height
  },
  footer: {
    marginTop: 8,
    position: "sticky",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 2,
    padding: "8px 16px calc(16px + var(--ion-safe-area-bottom, 0))",
    backgroundColor: Colors.white200,
  },
  actionButton: {
    [Breakpoints.mediumDeviceAndUp]: {
      minWidth: 210,
      width: "fit-content",
    },
  },
  secondaryButton: {
    display: "none",
    [Breakpoints.mediumDeviceAndUp]: {
      display: "block",
    },
  },
  buttonGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    [Breakpoints.mediumDeviceAndUp]: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",

      "& > *:first-child": {
        order: 2, // Primary button on the right
      },

      "& > *:last-child": {
        order: 1, // Secondary button on the left
      },
    },
  },
} satisfies Record<string, CSSObject>;
